/**
 * "On-Demand Access" — guided wizard for business/sales to grant or remove a
 * project's access to the platform's shared on-demand (by_request) pools.
 *
 * Behind the curtain each grant is one project-level pool inclusion
 * (onlyDefaultVersion: true) applied via membership:apply; removal deletes
 * that inclusion. Projects covered by an organization-wide grant are shown
 * as such and left to operators — removing an org grant would affect every
 * project in the org.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { usePlatformAdmin } from '../../../hooks';
import { SUPER_REGION_OPTIONS } from '../../../constants/regions';
import {
  CustomerPicker,
  getApiBaseUrl,
  Stepper,
  WizardActions,
  type CustomerSelection,
} from '../../../components/pool-wizards/wizardShared';
import styles from '../../../components/pool-wizards/PoolWizards.module.css';

export const Route = createFileRoute('/admin/pools/on-demand-access')({
  component: OnDemandAccessWizard,
});

const STEPS = ['Customer', 'Access', 'Review'];

interface OnDemandPoolRow {
  poolId: string;
  poolName: string;
  regionLabel: string;
  /** Inclusion id when the project has a direct grant on this pool. */
  projectInclusionId: string | null;
  /** True when an org-wide grant already covers this project (read-only here). */
  coveredByOrg: boolean;
  /** Desired state after the wizard (checkbox). */
  wanted: boolean;
}

type ApplyState = 'pending' | 'running' | 'done' | 'error';

function regionLabel(code: string): string {
  return SUPER_REGION_OPTIONS.find(r => r.value === code)?.label ?? code;
}

function OnDemandAccessWizard() {
  const { isPlatformAdmin } = usePlatformAdmin();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [customer, setCustomer] = useState<CustomerSelection>({ orgId: '', orgName: '', projectId: '', projectName: '' });
  const [rows, setRows] = useState<OnDemandPoolRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [applying, setApplying] = useState(false);
  const [applyResults, setApplyResults] = useState<Map<string, ApplyState>>(new Map());
  const [applyError, setApplyError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  useEffect(() => {
    if (!isPlatformAdmin) navigate({ to: '/' });
  }, [isPlatformAdmin, navigate]);

  const authHeaders = useCallback(async () => {
    const token = await getToken({ template: 'default' });
    if (!token) throw new Error('Authentication required');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getToken]);

  // Load on-demand pools + this project's current access when entering step 2.
  const loadAccess = useCallback(async () => {
    if (!customer.projectId) return;
    setLoadingRows(true);
    setLoadError(null);
    try {
      const headers = await authHeaders();
      const base = getApiBaseUrl();
      const poolsRes = await fetch(`${base}/api/admin/pools`, { headers });
      if (!poolsRes.ok) throw new Error(`HTTP ${poolsRes.status}`);
      const poolsData = await poolsRes.json() as { pools: Array<{ id: string; name: string; type: string; region: string; active: boolean }> };
      const onDemand = (poolsData.pools ?? []).filter(p => p.type === 'by_request' && p.active);

      const built = await Promise.all(onDemand.map(async (pool): Promise<OnDemandPoolRow> => {
        let projectInclusionId: string | null = null;
        let coveredByOrg = false;
        try {
          const memRes = await fetch(`${base}/api/admin/pools/${pool.id}/membership`, { headers });
          if (memRes.ok) {
            const mem = await memRes.json() as { inclusions?: Array<{ id: string; nodeKind: string; nodeId: string }> };
            for (const inc of mem.inclusions ?? []) {
              if (inc.nodeKind === 'project' && inc.nodeId === customer.projectId) projectInclusionId = inc.id;
              if (inc.nodeKind === 'org' && inc.nodeId === customer.orgId) coveredByOrg = true;
            }
          }
        } catch { /* row renders as no-access; toggling will surface errors */ }
        return {
          poolId: pool.id,
          poolName: pool.name,
          regionLabel: regionLabel(pool.region),
          projectInclusionId,
          coveredByOrg,
          wanted: projectInclusionId !== null || coveredByOrg,
        };
      }));
      built.sort((a, b) => a.regionLabel.localeCompare(b.regionLabel) || a.poolName.localeCompare(b.poolName));
      setRows(built);
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load on-demand pools');
    } finally {
      setLoadingRows(false);
    }
  }, [customer.projectId, customer.orgId, authHeaders]);

  const goToAccess = useCallback(() => {
    setStep(1);
    loadAccess();
  }, [loadAccess]);

  const toggleRow = (poolId: string) => {
    setRows(prev => prev.map(r => (r.poolId === poolId && !r.coveredByOrg ? { ...r, wanted: !r.wanted } : r)));
  };

  const adds = rows.filter(r => r.wanted && !r.coveredByOrg && r.projectInclusionId === null);
  const removes = rows.filter(r => !r.wanted && r.projectInclusionId !== null);
  const changes = [...adds, ...removes];

  const handleApply = useCallback(async () => {
    if (changes.length === 0) return;
    setApplying(true);
    setApplyError(null);
    const results = new Map<string, ApplyState>(changes.map(c => [c.poolId, 'pending' as ApplyState]));
    setApplyResults(new Map(results));

    const base = getApiBaseUrl();
    let anyFailed = false;
    try {
      const headers = await authHeaders();
      for (const change of changes) {
        results.set(change.poolId, 'running');
        setApplyResults(new Map(results));
        const op = change.projectInclusionId === null
          ? { kind: 'addInclusion', payload: { nodeKind: 'project', nodeId: customer.projectId, onlyDefaultVersion: true } }
          : { kind: 'removeInclusion', id: change.projectInclusionId };
        try {
          const res = await fetch(`${base}/api/admin/pools/${change.poolId}/membership:apply`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ ops: [op] }),
          });
          const data = await res.json().catch(() => ({})) as { ok?: boolean };
          const ok = res.ok && data.ok !== false;
          results.set(change.poolId, ok ? 'done' : 'error');
          if (!ok) anyFailed = true;
        } catch {
          results.set(change.poolId, 'error');
          anyFailed = true;
        }
        setApplyResults(new Map(results));
      }
      if (anyFailed) {
        setApplyError('Some changes did not apply. Nothing else was affected — try again, or contact operations.');
      } else {
        setSucceeded(true);
      }
    } catch (err: any) {
      setApplyError(err.message || 'Failed to apply changes');
    } finally {
      setApplying(false);
    }
  }, [changes, customer.projectId, authHeaders]);

  if (!isPlatformAdmin) return null;

  // ---- Success screen ----
  if (succeeded) {
    return (
      <div className={styles.page}>
        <div className={styles.successCard}>
          <h2 className={styles.successTitle}>✓ On-demand access updated</h2>
          <p className={styles.reviewSummary}>
            <strong>{customer.orgName} / {customer.projectName}</strong> now has on-demand access in:{' '}
            <strong>{rows.filter(r => r.wanted).map(r => r.regionLabel).join(', ') || 'no regions'}</strong>.
            Changes take effect within a minute.
          </p>
        </div>
        <div className={styles.actions}>
          <Link to="/admin/pools" className={styles.secondaryButton}>Back to Pools</Link>
          <button
            className={styles.primaryButton}
            onClick={() => { setSucceeded(false); setStep(0); setCustomer({ orgId: '', orgName: '', projectId: '', projectName: '' }); setRows([]); }}
          >
            Update Another Project
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <button className={styles.backLink} onClick={() => navigate({ to: '/admin/pools' })}>
        &larr; Pools
      </button>
      <h1 className={styles.title}>On-Demand Access</h1>
      <p className={styles.subtitle}>
        Choose which shared on-demand regions a customer's project can use. No dedicated machines —
        capacity is shared and starts on demand.
      </p>

      <Stepper steps={STEPS} current={step} />

      {/* Step 1 — Customer */}
      {step === 0 && (
        <div className={styles.stepCard}>
          <h2 className={styles.stepQuestion}>Whose access are you changing?</h2>
          <p className={styles.stepHint}>Pick the customer's organization and project.</p>
          <CustomerPicker authHeaders={authHeaders} value={customer} onChange={setCustomer} />
        </div>
      )}

      {/* Step 2 — Access */}
      {step === 1 && (
        <div className={styles.stepCard}>
          <h2 className={styles.stepQuestion}>Where should {customer.projectName || 'this project'} run on demand?</h2>
          <p className={styles.stepHint}>
            Checked regions accept this project's apps. Unchecking removes access — running sessions are not interrupted.
          </p>
          {loadError && <div className={styles.errorBox}>{loadError}</div>}
          {loadingRows ? (
            <div className={styles.stepHint}>Checking current access…</div>
          ) : rows.length === 0 ? (
            <div className={styles.stepHint}>No active on-demand pools exist right now.</div>
          ) : (
            rows.map(row => (
              <label key={row.poolId} className={styles.optionRow} style={row.coveredByOrg ? { opacity: 0.7, cursor: 'default' } : undefined}>
                <input
                  type="checkbox"
                  checked={row.wanted}
                  onChange={() => toggleRow(row.poolId)}
                  disabled={row.coveredByOrg}
                />
                <span>
                  {row.poolName} <span className={styles.optionRowNote}>{row.regionLabel}</span>
                  {row.coveredByOrg && (
                    <div className={styles.optionRowNote}>
                      Covered by an organization-wide grant — ask operations to change it.
                    </div>
                  )}
                  {!row.coveredByOrg && row.projectInclusionId !== null && (
                    <div className={styles.optionRowNote}>Currently has access</div>
                  )}
                </span>
              </label>
            ))
          )}
        </div>
      )}

      {/* Step 3 — Review */}
      {step === 2 && (
        <div className={styles.stepCard}>
          <h2 className={styles.stepQuestion}>Confirm the changes</h2>
          {changes.length === 0 ? (
            <p className={styles.stepHint}>No changes — the current access already matches your selection.</p>
          ) : (
            <>
              <ul className={styles.reviewList}>
                {adds.map(r => (
                  <li key={r.poolId}>
                    + Give <strong>{customer.projectName}</strong> access to {r.poolName} ({r.regionLabel})
                    {applyResults.get(r.poolId) === 'done' && ' ✓'}
                    {applyResults.get(r.poolId) === 'error' && ' ✗'}
                  </li>
                ))}
                {removes.map(r => (
                  <li key={r.poolId}>
                    − Remove access to {r.poolName} ({r.regionLabel})
                    {applyResults.get(r.poolId) === 'done' && ' ✓'}
                    {applyResults.get(r.poolId) === 'error' && ' ✗'}
                  </li>
                ))}
              </ul>
              {removes.length > 0 && (
                <div className={styles.warnLine}>
                  After removal, users in those regions will no longer get on-demand capacity for this
                  project. Sessions already running are not interrupted.
                </div>
              )}
            </>
          )}
          {applyError && <div className={styles.errorBox} style={{ marginTop: 'var(--space-3)' }}>{applyError}</div>}
        </div>
      )}

      <WizardActions
        onBack={step > 0 ? () => setStep(step - 1) : undefined}
        backDisabled={applying}
        onNext={step === 0 ? goToAccess : step === 1 ? () => setStep(2) : handleApply}
        nextLabel={step < 2 ? 'Next' : applying ? 'Applying…' : 'Apply Changes'}
        nextDisabled={
          applying ||
          (step === 0 && !(customer.orgId && customer.projectId)) ||
          (step === 1 && loadingRows) ||
          (step === 2 && changes.length === 0)
        }
      />
    </div>
  );
}
